// One run's cases: verdict chip, question, expected vs answer, grounding
// issues, cost. The judge's reason and the tool calls open under a row.

import { useState } from 'react';

import type { EvalResultRead } from '../../services/fastpathQuality';
import { clip, expectedDetail, expectedSummary, groundingIssues, usd } from './format';
import styles from '../FastpathQualityPage.module.css';

interface Props {
  results: EvalResultRead[];
  onToggle?: (caseId: string, open: boolean) => void;
}

export function EvalResultsTable({ results, onToggle }: Props) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (id: string) => {
    setOpen((prev) => {
      const next = new Set(prev);
      const willOpen = !next.has(id);
      if (willOpen) next.add(id);
      else next.delete(id);
      onToggle?.(id, willOpen);
      return next;
    });
  };
  return (
    <section className={styles.panel} aria-label="Cases">
      <div className={styles.panelHead}>
        <h2>Cases</h2>
        <span className={styles.meta}>{results.length} cases · click a row for the judge's reason and tool calls</span>
      </div>
      {results.length === 0 ? (
        <p className={styles.empty}>This run recorded no cases.</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>verdict</th>
                <th>case</th>
                <th>kind</th>
                <th>question</th>
                <th>expected</th>
                <th>answer</th>
                <th>grounding</th>
                <th className={styles.num}>fast / judge cost</th>
                <th className={styles.num}>latency</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => {
                const issues = groundingIssues(r.grounding);
                const expanded = open.has(r.id);
                return [
                  <tr
                    key={r.id}
                    className={`${styles.clickable} ${expanded ? styles.selectedRow : ''}`}
                    onClick={() => toggle(r.id)}
                    data-verdict={r.verdict}
                    data-case-id={r.case_id}
                  >
                    <td><span className={`${styles.chip} ${styles[`verdict_${r.verdict}`] ?? ''}`}>{r.verdict}</span></td>
                    <td><code className={styles.tag}>{r.case_id}</code>{r.routed_fast ? '' : <span className={styles.meta}> (not routed fast)</span>}</td>
                    <td>{r.kind}</td>
                    <td className={styles.wrap}>{r.question}</td>
                    <td className={styles.wrap}>{expectedSummary(r.expected)}</td>
                    <td className={styles.wrap}>{r.answer ? clip(r.answer) : (r.error ? <span className={styles.errText}>{r.error}</span> : '–')}</td>
                    <td className={`${styles.wrap} ${issues ? styles.flaggedCell : ''}`}>{issues || '–'}</td>
                    <td className={styles.num}>{usd(r.fast_cost_usd)} / {usd(r.judge_cost_usd)}</td>
                    <td className={styles.num}>{r.latency_ms.toLocaleString()} ms</td>
                  </tr>,
                  expanded ? (
                    <tr key={`${r.id}-detail`} className={styles.detailRow} data-testid={`case-detail-${r.case_id}`}>
                      <td colSpan={9}>
                        <div className={styles.detail}>
                          <div><strong>Judge:</strong> {r.reason ?? '–'}</div>
                          {r.error ? <div className={styles.errText}><strong>Error:</strong> {r.error}</div> : null}
                          {r.answer ? <div><strong>Answer:</strong><pre className={styles.pre}>{r.answer}</pre></div> : null}
                          {(() => {
                            const detail = expectedDetail(r.expected);
                            return (
                              <>
                                {detail.rows ? <div><strong>Truth rows:</strong><pre className={styles.pre}>{detail.rows}</pre></div> : null}
                                {detail.routing ? <div><strong>Routing:</strong><pre className={styles.pre}>{detail.routing}</pre></div> : null}
                              </>
                            );
                          })()}
                          <div>
                            <strong>Tool calls ({r.tool_calls.length}):</strong>
                            {r.tool_calls.length === 0 ? ' none' : (
                              <ol className={styles.toolCalls}>
                                {r.tool_calls.map((tc, i) => (
                                  <li key={i}>
                                    <code className={styles.tag}>{tc.tool ?? '?'}</code>{' '}
                                    <span className={styles.meta}>{typeof tc.input === 'string' ? tc.input : JSON.stringify(tc.input ?? null)}</span>
                                    {tc.result_excerpt ? <pre className={styles.pre}>{tc.result_excerpt}</pre> : null}
                                  </li>
                                ))}
                              </ol>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : null,
                ];
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
